import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { printScreenshots } from '@messenger/utils'
import Text from '@/components/priority/Text/Text'
import Button from '@/components/priority/Button/Button'
import { getProofGallery } from '@/api/proofGallery'
import * as S from '@/components/proof/ProofGallery.styled'

type ProofGalleryRouteParameters = {
  pdfId?: string
  token?: string
}

export const ProofGallery = () => {
  const { pdfId, token } = useParams<ProofGalleryRouteParameters>()
  const { t } = useTranslation()

  const [galleryScreenshotUrls, setGalleryScreenshotUrls] = useState<string[]>(
    [],
  )
  const [isGalleryLoading, setIsGalleryLoading] = useState<boolean>(true)
  const [galleryErrorMessage, setGalleryErrorMessage] = useState<string | null>(
    null,
  )
  const [activeScreenshotIndex, setActiveScreenshotIndex] = useState<number>(0)

  useEffect(() => {
    let galleryRequestIsActive = true

    const loadProofGallery = async () => {
      setIsGalleryLoading(true)
      setGalleryErrorMessage(null)
      setGalleryScreenshotUrls([])

      const galleryRouteMissingValues = !pdfId || !token
      if (galleryRouteMissingValues) {
        if (galleryRequestIsActive) {
          setGalleryErrorMessage(t('proofGallery.invalidLinkError'))
          setIsGalleryLoading(false)
        }
        return
      }

      try {
        const response = await getProofGallery(pdfId, token)

        const fetchedScreenshotUrls = response.data?.screenshotUrls ?? []

        if (galleryRequestIsActive) {
          if (fetchedScreenshotUrls.length === 0) {
            setGalleryErrorMessage(t('proofGallery.noProofAvailableMessage'))
          } else {
            setGalleryScreenshotUrls(fetchedScreenshotUrls)
            setActiveScreenshotIndex(0)
          }
        }
      } catch (error) {
        console.error(
          'error_loading_proof_gallery',
          (error as Error)?.message || 'Unknown error',
        )
        if (galleryRequestIsActive) {
          setGalleryErrorMessage(t('proofGallery.genericErrorMessage'))
        }
      } finally {
        if (galleryRequestIsActive) {
          setIsGalleryLoading(false)
        }
      }
    }

    void loadProofGallery()

    return () => {
      galleryRequestIsActive = false
    }
  }, [pdfId, token, t])

  const handleGoToPreviousScreenshot = () => {
    const isAlreadyAtBeginning = activeScreenshotIndex === 0
    if (isAlreadyAtBeginning) {
      return
    }

    const newScreenshotIndex = activeScreenshotIndex - 1
    setActiveScreenshotIndex(newScreenshotIndex)
  }

  const handleGoToNextScreenshot = () => {
    const isAlreadyAtEnd =
      activeScreenshotIndex === galleryScreenshotUrls.length - 1
    if (isAlreadyAtEnd) {
      return
    }

    const newScreenshotIndex = activeScreenshotIndex + 1
    setActiveScreenshotIndex(newScreenshotIndex)
  }

  const handlePrint = useCallback(() => {
    printScreenshots(galleryScreenshotUrls)
  }, [galleryScreenshotUrls])

  if (isGalleryLoading) {
    return (
      <S.ProofGalleryMessageWrapper>
        <Text size="18px" weight="500" align="center" color="#1b1f3b">
          {t('proofGallery.loadingMessage')}
        </Text>
      </S.ProofGalleryMessageWrapper>
    )
  }

  if (galleryErrorMessage) {
    return (
      <S.ProofGalleryMessageWrapper>
        <Text
          role="alert"
          size="18px"
          weight="500"
          align="center"
          color="#1b1f3b"
        >
          {galleryErrorMessage}
        </Text>
      </S.ProofGalleryMessageWrapper>
    )
  }

  const noScreenshotsAvailable = galleryScreenshotUrls.length === 0
  if (noScreenshotsAvailable) {
    return (
      <S.ProofGalleryMessageWrapper>
        <Text
          role="alert"
          size="18px"
          weight="500"
          align="center"
          color="#1b1f3b"
        >
          {t('proofGallery.noProofAvailableMessage')}
        </Text>
      </S.ProofGalleryMessageWrapper>
    )
  }

  const activeScreenshotUrl = galleryScreenshotUrls[activeScreenshotIndex]
  const totalScreenshotCount = galleryScreenshotUrls.length
  const displayedPageNumber = activeScreenshotIndex + 1

  return (
    <S.ProofGalleryContainer>
      <S.ProofGalleryHeader>
        <Text
          as="h1"
          size="28px"
          weight="600"
          color="#1b1f3b"
          style={{ margin: 0 }}
        >
          {t('proofGallery.title')}
        </Text>
        <S.ProofGalleryHeaderActions>
          <Text size="16px" weight="500" color="#4a4f68">
            {t('proofGallery.pageIndicatorText', {
              currentPageNumber: displayedPageNumber,
              totalPageCount: totalScreenshotCount,
            })}
          </Text>
          <Button
            type="button"
            primary
            padding="8px 16px"
            onClick={handlePrint}
            aria-label={t('proofGallery.printAriaLabel')}
          >
            {t('proofGallery.printLabel')}
          </Button>
        </S.ProofGalleryHeaderActions>
      </S.ProofGalleryHeader>

      <S.ProofGalleryImageWrapper>
        <S.ProofGalleryImage
          src={activeScreenshotUrl}
          alt={t('proofGallery.imageAltText', {
            pageNumber: displayedPageNumber,
          })}
        />
      </S.ProofGalleryImageWrapper>

      <S.ProofGalleryNavigation
        aria-label={t('proofGallery.navigationAriaLabel')}
      >
        <Button
          type="button"
          center
          primary
          padding="12px 24px"
          minWidth="140px"
          onClick={handleGoToPreviousScreenshot}
          disabled={activeScreenshotIndex === 0}
          aria-label={t('proofGallery.previousButtonAriaLabel')}
        >
          {t('proofGallery.previousButtonLabel')}
        </Button>
        <Button
          type="button"
          center
          primary
          padding="12px 24px"
          minWidth="140px"
          onClick={handleGoToNextScreenshot}
          disabled={activeScreenshotIndex === totalScreenshotCount - 1}
          aria-label={t('proofGallery.nextButtonAriaLabel')}
        >
          {t('proofGallery.nextButtonLabel')}
        </Button>
      </S.ProofGalleryNavigation>
    </S.ProofGalleryContainer>
  )
}

export default ProofGallery
